import { useEffect, useRef, useState } from 'react';
import {
  SOCCER_BALL_R,
  SOCCER_FIELD_H,
  SOCCER_FIELD_W,
  SOCCER_GOAL_HALF_HEIGHT,
  SOCCER_MAX_POWER,
  SOCCER_PUCK_R,
  type SoccerBody,
  type SoccerView,
} from '@minigame-race/contracts';
import { color } from '../theme.js';

/**
 * Soccer (Soccer Stars) — flick your pucks to knock the ball into the rival's
 * goal. On your turn, drag back from one of YOUR pucks (a slingshot: the flick
 * goes opposite the drag, power = drag length) and release. The server simulates
 * the shot and returns a trajectory we animate ONCE, keyed on `moveSeq` so the
 * ~100ms board re-push never restarts or duplicates the animation.
 */

// Drag length (in field units) that maps to full power. A drag roughly a third of
// the field is a hard flick.
const FULL_POWER_DRAG = 150;

export function SoccerBoard({
  view,
  onMove,
}: {
  view: SoccerView & { yourSeat: 0 | 1; yourTurn: boolean; moveMsLeft: number; moveCapMs: number };
  onMove: (move: unknown) => void;
}) {
  const { bodies, goals, lastPath, moveSeq, scoredSeat, celebrating, yourSeat, yourTurn, over, winnerSeat, moveMsLeft, moveCapMs } = view;
  const svgRef = useRef<SVGSVGElement | null>(null);

  const [drag, setDrag] = useState<{ puckIndex: number; cx: number; cy: number; px: number; py: number } | null>(null);

  // One-shot animation of the last flick, keyed on moveSeq.
  const [anim, setAnim] = useState<SoccerBody[] | null>(null);
  const playedSeq = useRef(0);

  useEffect(() => {
    // Only animate when a NEW flick arrives (moveSeq advanced) and we have a path.
    if (moveSeq <= playedSeq.current || !lastPath || lastPath.length === 0) return;
    playedSeq.current = moveSeq;
    const frames = lastPath;
    // Play at REAL sim speed: the server samples the path every 6 physics steps
    // at dt=1/240s, so each sampled frame is 25ms of real motion. Advance the
    // animation by wall-clock elapsed / 25ms — so the ball and pucks move at a
    // natural pace instead of a fast blur.
    const MS_PER_FRAME = 25;
    const restingById = new Map(bodies.map((b) => [b.id, b]));
    let raf = 0;
    let cancelled = false;
    const startedAt = performance.now();
    const step = (): void => {
      if (cancelled) return;
      const elapsed = performance.now() - startedAt;
      const idx = Math.floor(elapsed / MS_PER_FRAME);
      if (idx >= frames.length - 1) {
        setAnim(null); // settle on the authoritative resting `bodies`
        return;
      }
      setAnim(frames[idx]!.map((f) => bodyFromFrame(f, restingById)));
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
    // Depend only on moveSeq: `bodies`/`lastPath` change identity every tick, but
    // moveSeq gates us so we never restart mid-animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moveSeq]);

  const shown = anim ?? bodies;
  const animating = anim !== null;
  const canInteract = yourTurn && !over && !animating && !celebrating;
  const scoredYou = scoredSeat === yourSeat;

  const toField = (e: React.PointerEvent): { x: number; y: number } => {
    const svg = svgRef.current!;
    const r = svg.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) / r.width) * SOCCER_FIELD_W,
      y: ((e.clientY - r.top) / r.height) * SOCCER_FIELD_H,
    };
  };

  const onPointerDown = (e: React.PointerEvent): void => {
    if (!canInteract) return;
    const { x, y } = toField(e);
    const puck = bodies.find(
      (b) => b.kind === 'puck' && b.seat === yourSeat && Math.hypot(b.x - x, b.y - y) < SOCCER_PUCK_R + 10,
    );
    if (!puck) return;
    const idx = Number(puck.id.slice(3)); // 's0p3' -> 3
    svgRef.current?.setPointerCapture(e.pointerId);
    setDrag({ puckIndex: idx, cx: puck.x, cy: puck.y, px: x, py: y });
  };

  const onPointerMove = (e: React.PointerEvent): void => {
    if (!drag) return;
    const { x, y } = toField(e);
    setDrag({ ...drag, px: x, py: y });
  };

  const onPointerUp = (e: React.PointerEvent): void => {
    if (!drag) return;
    try { svgRef.current?.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    // Slingshot: flick goes OPPOSITE the drag; power scales with drag length.
    const dx = drag.cx - drag.px;
    const dy = drag.cy - drag.py;
    const len = Math.hypot(dx, dy);
    setDrag(null);
    if (len < 8) return; // too small — ignore
    const power = Math.min(SOCCER_MAX_POWER, (len / FULL_POWER_DRAG) * SOCCER_MAX_POWER);
    onMove({ type: 'flick', puckIndex: drag.puckIndex, vx: (dx / len) * power, vy: (dy / len) * power });
  };

  const youGoals = goals[yourSeat];
  const rivalGoals = goals[yourSeat === 0 ? 1 : 0];
  const clockPct = moveCapMs > 0 ? Math.max(0, Math.min(1, moveMsLeft / moveCapMs)) : 0;
  // Aim preview: opposite the drag, length capped for a clean indicator.
  const aim = drag
    ? (() => {
        const dx = drag.cx - drag.px;
        const dy = drag.cy - drag.py;
        const len = Math.hypot(dx, dy) || 1;
        const shown = Math.min(len, FULL_POWER_DRAG);
        return { x: drag.cx + (dx / len) * shown, y: drag.cy + (dy / len) * shown, pow: Math.min(1, len / FULL_POWER_DRAG) };
      })()
    : null;

  return (
    <div style={{ width: 'min(94cqw, 460px)', height: '100cqh', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'min(1.6cqh,10px)', minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Score label="YOU" value={youGoals} tint={color.lime} />
        <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 800, color: over ? (winnerSeat === yourSeat ? color.lime : winnerSeat === null ? color.amber : color.redBright) : yourTurn ? color.lime : 'rgba(234,255,234,.5)' }}>
          {over ? (winnerSeat === yourSeat ? 'YOU WIN!' : winnerSeat === null ? 'DRAW' : 'RIVAL WINS') : animating ? '…' : yourTurn ? 'YOUR FLICK' : 'RIVAL’S TURN'}
        </div>
        <Score label="RIVAL" value={rivalGoals} tint={color.cyan} align="right" />
      </div>

      {/* your move-timer bar */}
      <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,.08)', overflow: 'hidden', flexShrink: 0 }}>
        {yourTurn && !over && !animating && (
          <div style={{ height: '100%', width: `${clockPct * 100}%`, background: clockPct < 0.3 ? color.red : color.lime, transition: 'width .1s linear' }} />
        )}
      </div>

      {/* the pitch fills the remaining height; width follows the field aspect and
          is centred, so the board fits any slot without overflow. */}
      <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${SOCCER_FIELD_W} ${SOCCER_FIELD_H}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => setDrag(null)}
        style={{
          height: '100%',
          maxWidth: '100%',
          aspectRatio: `${SOCCER_FIELD_W} / ${SOCCER_FIELD_H}`,
          borderRadius: 12,
          border: '1px solid rgba(166,255,46,.2)',
          background: 'linear-gradient(180deg,#12401f,#0e3319)',
          touchAction: 'none',
          cursor: canInteract ? 'grab' : 'default',
          display: 'block',
        }}
      >
        {/* pitch markings */}
        <line x1={SOCCER_FIELD_W / 2} y1={6} x2={SOCCER_FIELD_W / 2} y2={SOCCER_FIELD_H - 6} stroke="rgba(255,255,255,.18)" strokeWidth={2} />
        <circle cx={SOCCER_FIELD_W / 2} cy={SOCCER_FIELD_H / 2} r={42} fill="none" stroke="rgba(255,255,255,.18)" strokeWidth={2} />
        <circle cx={SOCCER_FIELD_W / 2} cy={SOCCER_FIELD_H / 2} r={3} fill="rgba(255,255,255,.3)" />
        {/* goals: your target glows lime (seat0 → right, seat1 → left) */}
        <GoalMouth side="left" mine={yourSeat === 1} />
        <GoalMouth side="right" mine={yourSeat === 0} />

        {/* aim slingshot line while dragging */}
        {aim && drag && (
          <>
            <line x1={drag.cx} y1={drag.cy} x2={aim.x} y2={aim.y} stroke={aim.pow > 0.85 ? color.redBright : color.limeBright} strokeWidth={3} strokeLinecap="round" strokeDasharray="7 5" />
            <circle cx={aim.x} cy={aim.y} r={5} fill={aim.pow > 0.85 ? color.redBright : color.limeBright} />
          </>
        )}

        {/* bodies */}
        {shown.map((b) => {
          if (b.kind === 'ball') {
            return <circle key={b.id} cx={b.x} cy={b.y} r={SOCCER_BALL_R} fill="#fbfff5" stroke="#0c1408" strokeWidth={1.5} />;
          }
          const mine = b.seat === yourSeat;
          const grabbable = canInteract && mine;
          return (
            <g key={b.id}>
              {grabbable && <circle cx={b.x} cy={b.y} r={SOCCER_PUCK_R + 3} fill="none" stroke={color.lime} strokeWidth={1} opacity={0.4} />}
              <circle
                cx={b.x}
                cy={b.y}
                r={SOCCER_PUCK_R}
                fill={mine ? color.lime : color.cyan}
                stroke={mine ? '#0c1408' : '#04222a'}
                strokeWidth={2}
              />
            </g>
          );
        })}
      </svg>

      {/* GOAL! overlay — shown only AFTER the shot animation lands the ball in
          the net, so the celebration doesn't flash while the ball is mid-flight. */}
      {celebrating && !animating && (
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
          <div style={{ textAlign: 'center', animation: 'nkPop .3s ease-out' }}>
            <div style={{ fontSize: 'clamp(30px,12cqmin,64px)', fontWeight: 900, letterSpacing: '.08em', color: scoredYou ? color.limeBright : color.cyanBright, textShadow: '0 3px 18px rgba(0,0,0,.7)' }}>
              GOAL!
            </div>
            <div style={{ fontSize: 'clamp(11px,3cqmin,16px)', fontWeight: 700, color: 'rgba(255,255,255,.85)', letterSpacing: '.04em' }}>
              {scoredYou ? 'You scored!' : 'Rival scored'}
            </div>
          </div>
        </div>
      )}
      </div>

      <div style={{ textAlign: 'center', fontSize: 'clamp(9px,1.7cqmin,12px)', flexShrink: 0, color: 'rgba(234,255,234,.45)' }}>
        {over ? 'Game over.' : celebrating ? '' : canInteract ? 'Drag back from a green piece, then release to flick.' : animating ? 'Watch the shot…' : 'Rival is aiming…'}
      </div>
    </div>
  );
}

function GoalMouth({ side, mine }: { side: 'left' | 'right'; mine: boolean }) {
  const y = SOCCER_FIELD_H / 2 - SOCCER_GOAL_HALF_HEIGHT;
  const h = SOCCER_GOAL_HALF_HEIGHT * 2;
  const x = side === 'left' ? 0 : SOCCER_FIELD_W - 6;
  const tint = mine ? color.lime : color.cyan;
  return <rect x={x} y={y} width={6} height={h} fill={tint} opacity={mine ? 0.6 : 0.35} rx={2} />;
}

function bodyFromFrame(f: { id: string; x: number; y: number }, byId: Map<string, SoccerBody>): SoccerBody {
  const base = byId.get(f.id);
  return { id: f.id, kind: base?.kind ?? 'puck', seat: base?.seat ?? null, x: f.x, y: f.y };
}

function Score({ label, value, tint, align }: { label: string; value: number; tint: string; align?: 'right' }) {
  return (
    <div style={{ textAlign: align === 'right' ? 'right' : 'left' }}>
      <div style={{ fontSize: 10, letterSpacing: '.2em', color: 'rgba(234,255,234,.4)' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: tint, lineHeight: 1 }}>{value}</div>
    </div>
  );
}
